import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
	MediaUpload,
	MediaUploadCheck
} from '@wordpress/block-editor';

import {
	PanelBody,
	SelectControl,
	RangeControl,
	Button,
	TextControl,
	ToggleControl
} from '@wordpress/components';

import './editor.scss';

export default function Edit({ attributes, setAttributes }) {

	const {
		logos,
		template,
		animationDuration,
		showAltText,
		logoSize,
		slideDirection
	} = attributes;

	const blockProps = useBlockProps();


	const isPro = window.wlcProData?.isPro || false;


	// Add Logo
	const addLogo = (media) => {

		const newLogos = [
			...logos,
			{
				id: media.id,
				url: media.url,
				alt: media.alt || '',
				link: '',
			}
		];

		setAttributes({ logos: newLogos });
	};

	// Update logo fields
	const updateLogo = (index, field, value) => {

		const newLogos = [...logos];
		newLogos[index][field] = value;

		setAttributes({ logos: newLogos });

	};


	// Remove logo
	const removeLogo = (index) => {

		const newLogos = logos.filter((_, i) => i !== index);
		setAttributes({ logos: newLogos });

	};

	const upgradeLink = 'https://checkout.freemius.com/plugin/24598/plan/40853/licenses/1/';
	const rateLink = 'https://wordpress.org/support/plugin/wbd-logo-carousel/reviews/#new-post';
	const pageLink = 'https://www.wpbranddigital.org/contact/';

	return (
		<>

			<InspectorControls>

				{/* PRO BUTTON */}
				<PanelBody title="Unlock Pro Features & Help">

					<p style={{ marginBottom: '10px' }}>
						Upgrade to the Pro version to unlock advanced features and theme.
					</p>

					<Button
						isPrimary
						onClick={() => window.open(upgradeLink, '_blank')}
						style={{ marginBottom: '10px', marginRight: '4px' }}
					>
						Get Pro Version
					</Button>
					<Button
						isPrimary
						onClick={() => window.open(rateLink, '_blank')}
					>
						Rate Plugin
					</Button>

					<Button
						isPrimary
						onClick={() => window.open(pageLink, '_blank')}

					>
						Contact Support
					</Button>		

				</PanelBody>
				{/* theme */}
				<PanelBody title="Theme Switch">
					<SelectControl
						label={__('Theme Style', 'wbd-logo-carousel')}
						value={template}
						options={[
							{ label: __('Classic', 'wbd-logo-carousel'), value: 'classic' },
							{ label: __('Elegant', 'wbd-logo-carousel'), value: 'elegant' },
							{ label: isPro ? __('Luxury', 'wbd-logo-carousel') : __('Luxury (Pro)', 'wbd-logo-carousel'), value: 'luxury' },
							{ label: isPro ? __('Modern', 'wbd-logo-carousel') : __('Modern (Pro)', 'wbd-logo-carousel'), value: 'modern' },
							{ label: isPro ? __('Minimal', 'wbd-logo-carousel') : __('Minimal (Pro)', 'wbd-logo-carousel'), value: 'minimal' },
							{ label: isPro ? __('Premium', 'wbd-logo-carousel') : __('Premium (Pro)', 'wbd-logo-carousel'), value: 'premium' }
						]}
						onChange={(value) => setAttributes({ template: value })}
					/>
				</PanelBody>

				{/* LOGO MANAGEMENT */}
				<PanelBody title="Manage Logos" initialOpen={true}>

					{logos.map((logo, index) => (

						<div key={index} className="logo-sidebar-card">

							<img className="logo-sidebar-image" src={logo.url} alt={logo.alt || ''} />

							<TextControl
								label="Link URL"
								value={logo.link}
								onChange={(value) => updateLogo(index, 'link', value)}
								placeholder="https://"
							/>

							<TextControl
								label="Alt Text"
								value={logo.alt}
								onChange={(value) => updateLogo(index, 'alt', value)}
							/>

							<Button
								isDestructive
								onClick={() => removeLogo(index)}
							>
								Remove Logo
							</Button>

						</div>

					))}

					<MediaUploadCheck>

						<MediaUpload
							onSelect={addLogo}
							allowedTypes={['image']}
							render={({ open }) => (

								<Button
									onClick={open}
									variant="primary"
									style={{ marginTop: '10px', width: "100%", display: 'flex', justifyContent: 'center' }}
								>
									+ Add Logo
								</Button>

							)}
						/>

					</MediaUploadCheck>

				</PanelBody>

				{/* CAROUSEL SETTINGS */}
				<PanelBody title="Carousel Settings">

					<SelectControl
						label="Slide Direction"
						value={slideDirection}
						options={[
							{ label: 'Left', value: 'left' },
							{ label: 'Right', value: 'right' }
						]}
						onChange={(value) => setAttributes({ slideDirection: value })}
					/>

					<RangeControl
						label="Animation Duration (seconds)"
						value={animationDuration}
						onChange={(value) => setAttributes({ animationDuration: value })}
						min={10}
						max={60}
						step={5}
					/>

					<RangeControl
						label="Logo Size (px)"
						value={logoSize}
						onChange={(value) => setAttributes({ logoSize: value })}
						min={50}
						max={300}
						step={10}
					/>

					<ToggleControl
						label="Show Alt Text"
						checked={showAltText}
						onChange={(value) => setAttributes({ showAltText: value })}
					/>

				</PanelBody>


			</InspectorControls>


			{/* LIVE PREVIEW */}
			<div {...blockProps}>

				<div
					className={`logo-carousel logo-carousel--${template} logo-carousel--${slideDirection}`}
					style={{
						'--animation-duration': `${animationDuration}s`
					}}
				>
					{
						logos.concat(logos) == 0 && <>
							<div className='wlcs-add-logo-section'>
								<p>⚠️ No logos added. Please add logos from the sidebar.</p>
							</div>
						</>
					}

					<div className="logo-carousel__track">

						{logos.concat(logos).map((logo, index) => (

							<div key={index} className="logo-carousel__item">

								{logo.link ? (

									<a href={logo.link}>

										<img
											src={logo.url}
											alt={logo.alt}
											style={{ maxWidth: `${logoSize}px` }}
										/>

										{showAltText && logo.alt && (
											<span className="logo-carousel__alt-text">
												{logo.alt}
											</span>
										)}

									</a>

								) : (

									<>

										<img
											src={logo.url}
											alt={logo.alt}
											style={{ maxWidth: `${logoSize}px` }}
										/>

										{showAltText && logo.alt && (
											<span className="logo-carousel__alt-text">
												{logo.alt}
											</span>
										)}

									</>

								)}

							</div>

						))}

					</div>

				</div>

			</div>

		</>
	);
}